import React, { useState, useEffect } from 'react';
import { DocumentTextIcon, CloudArrowUpIcon, CheckCircleIcon, ExclamationTriangleIcon, AcademicCapIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { teachersApi, classesApi, linesApi, learnersApi } from '../services/api';
import DataTable from '../components/DataTable/DataTable';
import toast from 'react-hot-toast';
import type { Teacher, Class, Line } from '../types';

interface UploadStatus {
  isUploading: boolean;
  message: string;
  type: 'success' | 'error' | 'info';
}

const InputsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'teachers' | 'classes' | 'lines' | 'learners'>('teachers');
  const [teachersData, setTeachersData] = useState<Teacher[]>([]);
  const [classesData, setClassesData] = useState<Class[]>([]);
  const [linesData, setLinesData] = useState<Line[]>([]);
  const [validTriplets, setValidTriplets] = useState<any[]>([]);
  const [tripletCount, setTripletCount] = useState<number>(0);
  const [validPentuplets, setValidPentuplets] = useState<any[]>([]);
  const [pentupletCount, setPentupletCount] = useState<number>(0);
  const [learnersData, setLearnersData] = useState<any[]>([]);
  const [learnerStats, setLearnerStats] = useState<any>(null);
  const [missingTeachers, setMissingTeachers] = useState<Array<{ class_code: string; teacher_code: string; teacher_field: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus | null>(null);
  const [expandedGrades, setExpandedGrades] = useState<Set<number>>(new Set());

  const tabs = [
    { key: 'teachers', label: 'Teachers', description: 'Upload and manage teacher data' },
    { key: 'classes', label: 'Classes', description: 'Upload and manage class information' },
    { key: 'lines', label: 'Lines', description: 'View lines by grade' },
    { key: 'learners', label: 'Learners', description: 'Upload and manage learner data for custom timetables' },
  ] as const;

  const teacherColumns = [
    { key: 'code', label: 'Code' },
    { key: 'name', label: 'Name' },
    { key: 'surname', label: 'Surname' },
    { key: 'title', label: 'Title' },
    { key: 'room', label: 'Room' },
    { key: 'department', label: 'Department' },
  ];

  const classColumns = [
    { key: 'code', label: 'Code' },
    { key: 'grade', label: 'Grade' },
    { key: 'class_code', label: 'Class Code' },
    { key: 'subject_code', label: 'Subject Code' },
    { key: 'subject_section', label: 'Subject Section' },
    { key: 'allocated_periods', label: 'Periods' },
    { key: 'teacher1', label: 'Teacher 1' },
    { key: 'teacher2', label: 'Teacher 2' },
    { key: 'line', label: 'Line' },
  ];

  const lineColumns = [
    { key: 'code', label: 'Code' },
    { key: 'class_codes_list', label: 'Classes' },
    { key: 'grade', label: 'Grade' },
  ];

  const learnerColumns = [
    { key: 'learner_id', label: 'Student ID' },
    { key: 'name', label: 'Name' },
    { key: 'surname', label: 'Surname' },
    { key: 'grade', label: 'Grade' },
    { key: 'assessment_class', label: 'Assessment Class' },
    { key: 'curriculum_type', label: 'Curriculum' },
    { key: 'upload_section', label: 'Upload Section' },
  ];

  const fetchTeachers = async () => {
    try {
      const response = await teachersApi.getAll();
      setTeachersData(response.data);
    } catch (error) {
      console.error('Error loading teachers:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await classesApi.getAll();
      setClassesData(response.data);
      
      // Fetch missing teachers info
      const missingResponse = await classesApi.getMissingTeachers();
      setMissingTeachers(missingResponse.data);
    } catch (error) {
      console.error('Error loading classes:', error);
    }
  };

  const fetchLines = async () => {
    try {
      const response = await linesApi.getAll();
      setLinesData(response.data.lines);
      setValidTriplets(response.data.valid_triplets);
      setTripletCount(response.data.triplet_count);
      setValidPentuplets(response.data.valid_pentuplets);
      setPentupletCount(response.data.pentuplet_count);
    } catch (error) {
      console.error('Error loading lines:', error);
    }
  };

  const fetchLearners = async () => {
    try {
      const response = await learnersApi.getAll();
      setLearnersData(response.data);
    } catch (error) {
      console.error('Error loading learners:', error);
    }
  };

  const fetchLearnerStats = async () => {
    try {
      const response = await learnersApi.getStats();
      setLearnerStats(response.data);
    } catch (error) {
      console.error('Error loading learner stats:', error);
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchTeachers(),
        fetchClasses(),
        fetchLines(),
        fetchLearners(),
        fetchLearnerStats(),
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileUpload = async (type: 'teachers' | 'classes' | 'lines', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadStatus({ isUploading: true, message: 'Uploading file...', type: 'info' });

    try {
      let response;
      if (type === 'teachers') {
        response = await teachersApi.uploadExcel(file);
        await fetchTeachers();
      } else if (type === 'classes') {
        response = await classesApi.uploadExcel(file);
        await fetchClasses();
        await fetchLines(); // Refresh lines since they're generated from classes
      }

      if (response) {
        setUploadStatus({ 
          isUploading: false, 
          message: response.data.message, 
          type: 'success' 
        });
        
        toast.success(response.data.message);
      }
      
      // Clear file input
      event.target.value = '';
      
      // Clear upload status after 3 seconds
      setTimeout(() => setUploadStatus(null), 3000);

    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Upload failed';
      setUploadStatus({ 
        isUploading: false, 
        message: errorMessage, 
        type: 'error' 
      });
      
      toast.error(errorMessage);
      
      // Clear file input
      event.target.value = '';
      
      // Clear upload status after 5 seconds
      setTimeout(() => setUploadStatus(null), 5000);
    }
  };

  const handleLearnerUpload = async (section: 'caps-8-12' | 'cam-8-11', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const sectionName = section === 'caps-8-12' ? 'CAPS Grade 8-12' : 'CAM Grade 8-11';
    setUploadStatus({ isUploading: true, message: `Uploading ${sectionName} learners...`, type: 'info' });

    try {
      let response;
      if (section === 'caps-8-12') {
        response = await learnersApi.uploadCaps8to12(file);
      } else {
        response = await learnersApi.uploadCam8to11(file);
      }

      if (response) {
        setUploadStatus({ 
          isUploading: false, 
          message: response.data.message, 
          type: 'success' 
        });
        
        toast.success(response.data.message);
        
        // Refresh learners data and stats
        await fetchLearners();
        await fetchLearnerStats();
      }
      
      // Clear file input
      event.target.value = '';
      
      // Clear upload status after 3 seconds
      setTimeout(() => setUploadStatus(null), 3000);

    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Upload failed';
      setUploadStatus({ 
        isUploading: false, 
        message: errorMessage, 
        type: 'error' 
      });
      
      toast.error(errorMessage);
      
      // Clear file input
      event.target.value = '';
      
      // Clear upload status after 5 seconds
      setTimeout(() => setUploadStatus(null), 5000);
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'teachers': return teachersData;
      case 'classes': return classesData;
      case 'lines': return linesData;
      case 'learners': return learnersData;
      default: return [];
    }
  };

  const getCurrentColumns = () => {
    switch (activeTab) {
      case 'teachers': return teacherColumns;
      case 'classes': return classColumns;
      case 'lines': return lineColumns;
      case 'learners': return learnerColumns;
      default: return [];
    }
  };

  const getExpectedColumns = (type: string) => {
    switch (type) {
      case 'teachers': return 'code, name, surname, title, room, department';
      case 'classes': return 'code, grade, class_code, subject_code (optional), subject_section, allocated_periods, teacher1, teacher2 (optional), line';
      default: return '';
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center space-x-3">
        <DocumentTextIcon className="w-8 h-8 text-accent-teal" />
        <h1 className="text-h1 font-montserrat text-northwood-navy">Data Inputs</h1>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-northwood-silver">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-4 px-1 border-b-2 font-montserrat font-medium text-sm transition-colors duration-200 ${
                activeTab === tab.key
                  ? 'border-accent-teal text-accent-teal'
                  : 'border-transparent text-neutral-medium-gray hover:text-neutral-dark-gray'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Upload Status */}
      {uploadStatus && (
        <div className={`p-4 rounded-lg border-l-4 ${
          uploadStatus.type === 'success' 
            ? 'bg-semantic-success bg-opacity-10 border-semantic-success text-semantic-success' 
            : uploadStatus.type === 'error'
            ? 'bg-semantic-error bg-opacity-10 border-semantic-error text-semantic-error'
            : 'bg-semantic-info bg-opacity-10 border-semantic-info text-semantic-info'
        }`}>
          <div className="flex items-center space-x-3">
            {uploadStatus.type === 'success' ? (
              <CheckCircleIcon className="w-5 h-5" />
            ) : uploadStatus.type === 'error' ? (
              <ExclamationTriangleIcon className="w-5 h-5" />
            ) : (
              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            )}
            <span className="font-medium">{uploadStatus.message}</span>
          </div>
        </div>
      )}

      <div className={`grid ${activeTab === 'lines' ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'} gap-8`}>
        {/* Upload Section */}
        {activeTab !== 'lines' && (
          <div className="space-y-6">
            {tabs.map((tab) => (
              <div
                key={tab.key}
                className={activeTab === tab.key ? 'block' : 'hidden'}
              >
                <div className="card">
                {tab.key !== 'lines' && (
                  <>
                    <h2 className="text-h3 font-montserrat text-northwood-navy mb-2">
                      Upload {tab.label} Data
                    </h2>
                    <p className="text-neutral-medium-gray mb-6">{tab.description}</p>
                  </>
                )}

                {/* Teacher References Status for Classes Tab */}
                {tab.key === 'classes' && classesData.length > 0 && (
                  missingTeachers.length > 0 ? (
                    <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-montserrat font-semibold text-amber-900 mb-1">
                            Missing Teacher References
                          </h4>
                          <p className="text-sm text-amber-700 mb-2">
                            {missingTeachers.length} class{missingTeachers.length > 1 ? 'es have' : ' has'} teacher codes that don't exist in the teachers table. 
                            These classes will be assigned to room "Mismatch" when placed on the timetable.
                          </p>
                          <details className="text-xs text-amber-600">
                            <summary className="cursor-pointer hover:text-amber-800 font-medium">View affected classes</summary>
                            <ul className="mt-2 space-y-1 ml-4">
                              {missingTeachers.map((item, idx) => (
                                <li key={idx}>
                                  Class <code className="bg-amber-100 px-1 py-0.5 rounded">{item.class_code}</code> → 
                                  Teacher <code className="bg-amber-100 px-1 py-0.5 rounded">{item.teacher_code}</code> 
                                  ({item.teacher_field})
                                </li>
                              ))}
                            </ul>
                          </details>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6 p-4 bg-green-50 border border-green-300 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-montserrat font-semibold text-green-900 mb-1">
                            All Teacher References Valid
                          </h4>
                          <p className="text-sm text-green-700">
                            All {classesData.length} class{classesData.length > 1 ? 'es have' : ' has'} valid teacher codes that exist in the teachers table. 
                            Classes will be assigned to their teacher's rooms when placed on the timetable.
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                )}

                {/* File Upload Area - Special handling for learners */}
                {tab.key === 'learners' ? (
                  <div className="space-y-6">
                    {/* Learner Stats */}
                    {learnerStats && (
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <h4 className="font-montserrat font-semibold text-blue-900 mb-1">
                            CAPS Learners (Grade 8-12)
                          </h4>
                          <p className="text-2xl font-bold text-blue-700">
                            {learnerStats.caps_grade_8_12}
                          </p>
                        </div>
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                          <h4 className="font-montserrat font-semibold text-green-900 mb-1">
                            CAM Learners (Grade 8-11)
                          </h4>
                          <p className="text-2xl font-bold text-green-700">
                            {learnerStats.cam_grade_8_11}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* CAPS Upload Section */}
                    <div className="border border-blue-200 rounded-lg p-6">
                      <h4 className="font-montserrat font-semibold text-blue-900 mb-2">
                        CAPS Curriculum (Grade 8-12)
                      </h4>
                      <p className="text-sm text-blue-700 mb-4">
                        Upload Excel file containing CAPS learners for grades 8-12
                      </p>
                      
                      <div className="border-2 border-dashed border-blue-300 rounded-lg p-6 text-center">
                        <AcademicCapIcon className="w-10 h-10 text-blue-500 mx-auto mb-3" />
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={(e) => handleLearnerUpload('caps-8-12', e)}
                          className="hidden"
                          id="file-upload-caps"
                          disabled={uploadStatus?.isUploading}
                        />
                        <label
                          htmlFor="file-upload-caps"
                          className={`btn-primary cursor-pointer inline-block ${
                            uploadStatus?.isUploading ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {uploadStatus?.isUploading ? 'Uploading...' : 'Upload CAPS Learners'}
                        </label>
                      </div>
                    </div>

                    {/* CAM Upload Section */}
                    <div className="border border-green-200 rounded-lg p-6">
                      <h4 className="font-montserrat font-semibold text-green-900 mb-2">
                        CAM Curriculum (Grade 8-11)
                      </h4>
                      <p className="text-sm text-green-700 mb-4">
                        Upload Excel file containing CAM learners for grades 8-11
                      </p>
                      
                      <div className="border-2 border-dashed border-green-300 rounded-lg p-6 text-center">
                        <AcademicCapIcon className="w-10 h-10 text-green-500 mx-auto mb-3" />
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={(e) => handleLearnerUpload('cam-8-11', e)}
                          className="hidden"
                          id="file-upload-cam"
                          disabled={uploadStatus?.isUploading}
                        />
                        <label
                          htmlFor="file-upload-cam"
                          className={`btn-primary cursor-pointer inline-block ${
                            uploadStatus?.isUploading ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {uploadStatus?.isUploading ? 'Uploading...' : 'Upload CAM Learners'}
                        </label>
                      </div>
                    </div>

                    {/* Expected Format */}
                    <div className="p-4 bg-neutral-light-gray rounded-lg">
                      <h4 className="font-montserrat font-semibold text-neutral-dark-gray mb-2">
                        Expected Excel Columns:
                      </h4>
                      <div className="text-sm font-roboto text-neutral-medium-gray">
                        <code className="bg-white p-1 rounded">learner_id, name, surname, grade, assessment_class, class_list (comma-separated)</code>
                      </div>
                      <p className="text-xs text-neutral-medium-gray mt-2">
                        The curriculum type and upload section are automatically assigned based on the upload endpoint used.
                      </p>
                    </div>
                  </div>
                ) : tab.key !== 'lines' && (
                  <>
                    <div className="border-2 border-dashed border-northwood-silver rounded-lg p-8 text-center">
                      <CloudArrowUpIcon className="w-12 h-12 text-neutral-medium-gray mx-auto mb-4" />
                      <h3 className="text-lg font-montserrat font-semibold text-neutral-dark-gray mb-2">
                        Upload Excel File
                      </h3>
                      <p className="text-neutral-medium-gray mb-4">
                        Select an Excel file (.xlsx or .xls) containing your {tab.key} data
                      </p>
                      
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => handleFileUpload(tab.key, e)}
                        className="hidden"
                        id={`file-upload-${tab.key}`}
                        disabled={uploadStatus?.isUploading}
                      />
                      <label
                        htmlFor={`file-upload-${tab.key}`}
                        className={`btn-primary cursor-pointer inline-block ${
                          uploadStatus?.isUploading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {uploadStatus?.isUploading ? 'Uploading...' : 'Choose File'}
                      </label>
                    </div>

                    {/* Expected Format */}
                    <div className="mt-6 p-4 bg-neutral-light-gray rounded-lg">
                      <h4 className="font-montserrat font-semibold text-neutral-dark-gray mb-2">
                        Expected Excel Columns:
                      </h4>
                      <div className="text-sm font-roboto text-neutral-medium-gray">
                        <code className="bg-white p-1 rounded">{getExpectedColumns(tab.key)}</code>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
          </div>
        )}

        {/* Data Display Section */}
        <div>
          {activeTab === 'lines' ? (
            <div className="space-y-6">
              {/* Valid Triplets Summary */}
              <div className="card bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200">
                <div className="p-6">
                  <h2 className="text-h2 font-montserrat text-northwood-navy mb-4 flex items-center">
                    <CheckCircleIcon className="w-8 h-8 text-green-600 mr-3" />
                    Valid CAPS Triplets (Grade 10-12)
                  </h2>
                  
                  <div className="mb-6">
                    <div className="bg-white rounded-lg p-6 border border-blue-200 inline-block">
                      <p className="text-sm text-neutral-medium-gray mb-1">Total Valid Triplets</p>
                      <p className="text-4xl font-bold text-blue-700">{tripletCount}</p>
                    </div>
                  </div>

                  {validTriplets.length > 0 && (
                    <details className="mt-4">
                      <summary className="cursor-pointer text-lg font-montserrat font-semibold text-northwood-navy hover:text-accent-teal mb-3">
                        View All {tripletCount} Valid Triplet Combinations
                      </summary>
                      <div className="mt-4 space-y-3 max-h-96 overflow-y-auto">
                        {validTriplets.map((triplet, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-4 border border-neutral-light-gray">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="font-montserrat font-semibold text-northwood-navy mb-2">
                                  Triplet {idx + 1}
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                                  <div className="bg-blue-50 rounded p-2">
                                    <p className="text-xs text-blue-700 font-semibold">Grade 10</p>
                                    <p className="text-sm text-northwood-navy">{triplet.grade_10_line}</p>
                                  </div>
                                  <div className="bg-green-50 rounded p-2">
                                    <p className="text-xs text-green-700 font-semibold">Grade 11</p>
                                    <p className="text-sm text-northwood-navy">{triplet.grade_11_line}</p>
                                  </div>
                                  <div className="bg-yellow-50 rounded p-2">
                                    <p className="text-xs text-yellow-700 font-semibold">Grade 12</p>
                                    <p className="text-sm text-northwood-navy">{triplet.grade_12_line}</p>
                                  </div>
                                </div>
                                <div className="flex items-center text-sm text-neutral-medium-gray">
                                  <span className="font-semibold mr-2">Teachers ({triplet.total_teachers}):</span>
                                  <span className="text-xs">{triplet.teachers.join(', ')}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </div>

              {/* Valid Pentuplets Summary */}
              <div className="card bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200">
                <div className="p-6">
                  <h2 className="text-h2 font-montserrat text-northwood-navy mb-4 flex items-center">
                    <CheckCircleIcon className="w-8 h-8 text-green-600 mr-3" />
                    Valid Pentuplets (Gr10-12 CAPS + Gr10-11 Cambridge)
                  </h2>
                  
                  <div className="mb-6">
                    <div className="bg-white rounded-lg p-6 border border-green-200 inline-block">
                      <p className="text-sm text-neutral-medium-gray mb-1">Total Valid Pentuplets</p>
                      <p className="text-4xl font-bold text-green-700">{pentupletCount}</p>
                    </div>
                  </div>

                  {validPentuplets.length > 0 && (
                    <details className="mt-4">
                      <summary className="cursor-pointer text-lg font-montserrat font-semibold text-northwood-navy hover:text-accent-teal mb-3">
                        View All {pentupletCount} Valid Pentuplet Combinations
                      </summary>
                      <div className="mt-4 space-y-3 max-h-96 overflow-y-auto">
                        {validPentuplets.map((pentuplet, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-4 border border-neutral-light-gray">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="font-montserrat font-semibold text-northwood-navy mb-3">
                                  Pentuplet {idx + 1}
                                </p>
                                <div className="space-y-3 mb-3">
                                  <div className="border-l-4 border-blue-500 pl-3">
                                    <p className="text-xs text-blue-700 font-semibold mb-1">CAPS Lines</p>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                      <div className="bg-blue-50 rounded p-2">
                                        <p className="text-xs text-blue-700 font-semibold">Grade 10</p>
                                        <p className="text-sm text-northwood-navy">{pentuplet.grade_10_caps_line}</p>
                                      </div>
                                      <div className="bg-blue-50 rounded p-2">
                                        <p className="text-xs text-blue-700 font-semibold">Grade 11</p>
                                        <p className="text-sm text-northwood-navy">{pentuplet.grade_11_caps_line}</p>
                                      </div>
                                      <div className="bg-blue-50 rounded p-2">
                                        <p className="text-xs text-blue-700 font-semibold">Grade 12</p>
                                        <p className="text-sm text-northwood-navy">{pentuplet.grade_12_caps_line}</p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="border-l-4 border-green-500 pl-3">
                                    <p className="text-xs text-green-700 font-semibold mb-1">Cambridge Lines</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                      <div className="bg-green-50 rounded p-2">
                                        <p className="text-xs text-green-700 font-semibold">Grade 10</p>
                                        <p className="text-sm text-northwood-navy">{pentuplet.grade_10_cambridge_line}</p>
                                      </div>
                                      <div className="bg-green-50 rounded p-2">
                                        <p className="text-xs text-green-700 font-semibold">Grade 11</p>
                                        <p className="text-sm text-northwood-navy">{pentuplet.grade_11_cambridge_line}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center text-sm text-neutral-medium-gray">
                                  <span className="font-semibold mr-2">Teachers ({pentuplet.total_teachers}):</span>
                                  <span className="text-xs">{pentuplet.teachers.join(', ')}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </div>

              {/* Grade Lines Accordions */}
              {[8, 9, 10, 11, 12].map((grade) => {
                // Filter lines that start with (grade) pattern
                const gradeLines = linesData.filter(line => {
                  const match = line.code.match(/^\((\d+)\)/);
                  return match && parseInt(match[1]) === grade;
                });

                // Filter lines by curriculum type (determined by backend)
                const capsLines = gradeLines.filter(line => line.curriculum_type === 'CAPS');
                const cambridgeLines = gradeLines.filter(line => line.curriculum_type === 'Cambridge');

                const isExpanded = expandedGrades.has(grade);

                const toggleGrade = () => {
                  const newExpanded = new Set(expandedGrades);
                  if (isExpanded) {
                    newExpanded.delete(grade);
                  } else {
                    newExpanded.add(grade);
                  }
                  setExpandedGrades(newExpanded);
                };

                return (
                  <div key={grade} className="card">
                    <button
                      onClick={toggleGrade}
                      className="w-full flex items-center justify-between p-4 hover:bg-neutral-light-gray transition-colors duration-200"
                    >
                      <h3 className="text-h3 font-montserrat text-northwood-navy">
                        Grade {grade} Lines (CAPS: {capsLines.length}, Cambridge: {cambridgeLines.length})
                      </h3>
                      {isExpanded ? (
                        <ChevronUpIcon className="w-5 h-5 text-neutral-medium-gray" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5 text-neutral-medium-gray" />
                      )}
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-6">
                        {/* CAPS Lines */}
                        <div>
                          <h4 className="text-lg font-montserrat font-semibold text-blue-900 mb-3">
                            CAPS Lines ({capsLines.length})
                          </h4>
                          <DataTable
                            title=""
                            data={capsLines}
                            columns={lineColumns}
                            isLoading={isLoading}
                            emptyMessage={`No CAPS lines found for Grade ${grade}.`}
                          />
                        </div>

                        {/* Cambridge Lines */}
                        <div>
                          <h4 className="text-lg font-montserrat font-semibold text-green-900 mb-3">
                            Cambridge Lines ({cambridgeLines.length})
                          </h4>
                          <DataTable
                            title=""
                            data={cambridgeLines}
                            columns={lineColumns}
                            isLoading={isLoading}
                            emptyMessage={`No Cambridge lines found for Grade ${grade}.`}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <DataTable
              title={`Current ${tabs.find(t => t.key === activeTab)?.label} Data`}
              data={getCurrentData()}
              columns={getCurrentColumns()}
              isLoading={isLoading}
              emptyMessage={`No ${activeTab} data imported yet. Upload an Excel file to get started.`}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default InputsPage;